import { isLodgingPoi } from "../service";
import type {
  HotelMapCoordinate,
  HotelMapEvent,
  HotelMapEventHandler,
  HotelMapFactory,
  HotelMapInstance,
  HotelMapPoi,
} from "../types";
import {
  loadAmap,
  type AMapNamespace,
  type AMapNativeInfoWindow,
  type AMapNativeLngLat,
  type AMapNativeMap,
  type AMapNativeMarker,
  type LoadAmapOptions,
} from "./amapLoader";

/** 可注入的高德酒店地图工厂配置。 */
export type AmapHotelMapFactoryOptions = {
  loader?: (options: LoadAmapOptions) => Promise<AMapNamespace>;
  key?: string;
  securityCode?: string;
};

/** 创建使用高德 JS API 的酒店地图工厂。 */
export function createAmapHotelMapFactory(
  options: AmapHotelMapFactoryOptions = {},
): HotelMapFactory {
  const loader = options.loader ?? loadAmap;
  const key = options.key ?? import.meta.env.VITE_AMAP_JS_API_KEY ?? "";
  const securityCode = options.securityCode
    ?? import.meta.env.VITE_AMAP_SECURITY_JS_CODE
    ?? "";

  return {
    async create(container, createOptions) {
      const amap = await loader({ key, securityCode });
      const map = new amap.Map(container, {
        center: toPosition(createOptions.center),
        zoom: createOptions.zoom,
      });
      try {
        map.addControl(new amap.Scale());
        map.addControl(new amap.ToolBar());
        return createHotelMapInstance(amap, map);
      } catch (error) {
        try {
          map.destroy();
        } catch {
          // 保留地图装配的原始异常。
        }
        throw error;
      }
    },
  };
}

/** 生产环境默认使用的高德酒店地图工厂。 */
export const amapHotelMapFactory = createAmapHotelMapFactory();

type OwnedMarker = {
  marker: AMapNativeMarker;
  clickHandler: () => void;
};

/** 将高德原生地图包装为业务稳定接口。 */
function createHotelMapInstance(
  amap: AMapNamespace,
  map: AMapNativeMap,
): HotelMapInstance {
  const geolocation = new amap.Geolocation({
    enableHighAccuracy: true,
    timeout: 8000,
    maximumAge: 60000,
    showButton: false,
    showMarker: true,
    showCircle: true,
    panToLocation: false,
    zoomToAccuracy: false,
  });
  map.addControl(geolocation);
  let markers: OwnedMarker[] = [];
  let searchTarget: AMapNativeMarker | null = null;
  let infoWindow: AMapNativeInfoWindow | null = null;

  const closeInfoWindow = () => {
    infoWindow?.close();
    infoWindow = null;
  };

  const removeMarkers = () => {
    if (markers.length > 0) {
      markers.forEach(({ marker, clickHandler }) => marker.off("click", clickHandler));
      map.remove(markers.map(({ marker }) => marker));
      markers = [];
    }
  };

  const clearSearchTarget = () => {
    if (searchTarget) {
      const target = searchTarget;
      searchTarget = null;
      map.remove([target]);
    }
  };

  const bestEffortCleanup = (cleanup: () => void) => {
    try {
      cleanup();
    } catch {
      // 单项原生清理失败时继续释放其他地图资源。
    }
  };

  return {
    on(event, handler) {
      proxyMapEvent(map, "on", event, handler);
    },
    off(event, handler) {
      proxyMapEvent(map, "off", event, handler);
    },
    getViewport() {
      const bounds = map.getBounds();
      return {
        southwest: toCoordinate(bounds.getSouthWest()),
        northeast: toCoordinate(bounds.getNorthEast()),
        zoom: map.getZoom(),
      };
    },
    setCenterAndZoom(center, zoom) {
      map.setZoomAndCenter(zoom, toPosition(center));
    },
    setSearchTarget(coordinate) {
      clearSearchTarget();
      const content = document.createElement("div");
      content.className = "hotel-map-search-target";
      content.setAttribute("aria-hidden", "true");
      searchTarget = new amap.Marker({
        position: toPosition(coordinate),
        content,
        anchor: "center",
        zIndex: 120,
      });
      map.add([searchTarget]);
    },
    clearSearchTarget,
    replaceHotelMarkers(items) {
      closeInfoWindow();
      removeMarkers();
      markers = items.filter(isLodgingPoi).map((item) => {
        const marker = new amap.Marker({
          position: toPosition(item),
          content: createHotelMarkerContent(item),
          anchor: "center",
        });
        const clickHandler = () => {
          closeInfoWindow();
          infoWindow = new amap.InfoWindow({ content: createInfoWindowContent(item) });
          infoWindow.open(map, toPosition(item));
        };
        marker.on("click", clickHandler);
        return { marker, clickHandler };
      });
      if (markers.length > 0) {
        map.add(markers.map(({ marker }) => marker));
      }
    },
    locateCurrentPosition() {
      return new Promise<HotelMapCoordinate>((resolve, reject) => {
        const fail = () => reject(new Error("高德精确定位失败"));
        try {
          geolocation.getCurrentPosition((status, result) => {
            try {
              const coordinate = status === "complete"
                ? extractGeolocationCoordinate(result)
                : null;
              if (!coordinate) {
                fail();
                return;
              }
              resolve(coordinate);
            } catch {
              fail();
            }
          });
        } catch {
          fail();
        }
      });
    },
    locateCityCenter() {
      return new Promise<HotelMapCoordinate>((resolve, reject) => {
        const fail = () => reject(new Error("高德城市定位失败"));
        try {
          const citySearch = new amap.CitySearch();
          citySearch.getLocalCity((status, result) => {
            try {
              const center = status === "complete" ? extractCityCenter(result) : null;
              if (!center) {
                fail();
                return;
              }
              resolve(center);
            } catch {
              fail();
            }
          });
        } catch {
          fail();
        }
      });
    },
    destroy() {
      const ownedInfoWindow = infoWindow;
      infoWindow = null;
      if (ownedInfoWindow) {
        bestEffortCleanup(() => ownedInfoWindow.close());
      }

      const ownedMarkers = markers;
      markers = [];
      ownedMarkers.forEach(({ marker, clickHandler }) => {
        bestEffortCleanup(() => marker.off("click", clickHandler));
      });
      if (ownedMarkers.length > 0) {
        bestEffortCleanup(() => map.remove(ownedMarkers.map(({ marker }) => marker)));
      }

      bestEffortCleanup(clearSearchTarget);
      bestEffortCleanup(() => map.removeControl(geolocation));
      bestEffortCleanup(() => map.destroy());
    },
  };
}

/** 只向原生地图代理业务允许的视口事件。 */
function proxyMapEvent(
  map: AMapNativeMap,
  method: "on" | "off",
  event: HotelMapEvent,
  handler: HotelMapEventHandler,
): void {
  if (event !== "moveend" && event !== "zoomend") {
    return;
  }
  map[method](event, handler);
}

/** 使用安全 DOM API 构造房资源价格点位。 */
function createHotelMarkerContent(item: HotelMapPoi): HTMLElement {
  const content = document.createElement("div");
  content.className = "hotel-map-resource-marker";
  content.setAttribute("aria-label", item.name);
  content.textContent = formatReferencePrice(item) ?? "酒店";
  return content;
}

/** 使用安全 DOM API 构造房资源酒店信息窗内容。 */
function createInfoWindowContent(item: HotelMapPoi): HTMLElement {
  const content = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = item.name.trim() || "未命名酒店";
  content.append(title);
  appendDetail(content, "地址", item.address);
  appendDetail(content, "地区", item.regionText);
  appendDetail(content, "GEO 状态", formatGeoVerificationStatus(item.geoVerificationStatus));
  appendDetail(content, "房型", item.priceSkuName);
  appendDetail(content, "参考价", formatReferencePrice(item));
  appendDetail(content, "价格日期", item.priceServiceDate);
  return content;
}

/** 将内部 GEO 状态转换为管理员可识别的中文标签。 */
function formatGeoVerificationStatus(status: HotelMapPoi["geoVerificationStatus"]): string {
  return {
    unverified: "未核验",
    pending: "核验中",
    resolved: "已核验",
    review_required: "待人工复核",
    rejected: "已驳回",
    failed: "核验失败",
  }[status];
}

/** 将独立价格事实格式化为参考价，不把缺失价格伪装为零元。 */
function formatReferencePrice(item: HotelMapPoi): string | undefined {
  if (item.referencePrice === undefined) {
    return undefined;
  }
  const currency = item.currencyCode === "CNY" || !item.currencyCode
    ? "¥"
    : item.currencyCode;
  return `${currency}${item.referencePrice.toFixed(0)} 参考`;
}

/** 向信息窗追加一个非空纯文本字段。 */
function appendDetail(content: HTMLElement, label: string, value?: string): void {
  const normalizedValue = value?.trim();
  if (!normalizedValue) {
    return;
  }
  const detail = document.createElement("div");
  detail.textContent = `${label}：${normalizedValue}`;
  content.append(detail);
}

/** 从 Geolocation 的原生 position 提取高德已转换坐标。 */
function extractGeolocationCoordinate(result: unknown): HotelMapCoordinate | null {
  if (!isRecord(result) || !isRecord(result.position)) {
    return null;
  }
  const lng = callNumberMethod(result.position, "getLng");
  const lat = callNumberMethod(result.position, "getLat");
  if (lng === null || lat === null || !isValidCoordinate(lng, lat)) {
    return null;
  }
  return { lng, lat };
}

/** 从 CitySearch 的原生 bounds.getCenter 结果提取合法中心。 */
function extractCityCenter(result: unknown): HotelMapCoordinate | null {
  if (!isRecord(result) || !isRecord(result.bounds)) {
    return null;
  }
  const getCenter = result.bounds.getCenter;
  if (typeof getCenter !== "function") {
    return null;
  }

  const center: unknown = Reflect.apply(getCenter, result.bounds, []);
  if (!isRecord(center)) {
    return null;
  }
  const lng = callNumberMethod(center, "getLng");
  const lat = callNumberMethod(center, "getLat");
  if (lng === null || lat === null || !isValidCoordinate(lng, lat)) {
    return null;
  }
  return { lng, lat };
}

/** 调用原生对象的无参数字方法，并拒绝非有限结果。 */
function callNumberMethod(value: Record<string, unknown>, method: string): number | null {
  const candidate = value[method];
  if (typeof candidate !== "function") {
    return null;
  }
  const result: unknown = Reflect.apply(candidate, value, []);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

/** 判断坐标同时满足有限值与经纬度合法范围。 */
function isValidCoordinate(lng: number, lat: number): boolean {
  return lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
}

/** 将高德原生经纬度对象严格映射为领域坐标。 */
function toCoordinate(coordinate: AMapNativeLngLat): HotelMapCoordinate {
  return { lng: coordinate.getLng(), lat: coordinate.getLat() };
}

/** 将领域坐标转换为高德原生位置元组。 */
function toPosition(coordinate: HotelMapCoordinate): [number, number] {
  return [coordinate.lng, coordinate.lat];
}

/** 判断未知值是否为可读取字段的对象。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
